import React, { createContext, useContext, useMemo } from "react";
import {
  asArray,
  asText,
  evidenceText,
  formatPriority,
  getClipPath,
  getExecutiveBrief,
  getMeta,
  getReportBody,
  getWorkbook,
  metricValue,
  priorityTone,
  resolveMediaUrl,
  scoringLabel,
  timelineHasEnergy,
} from "../../utils/nativeReportUtils";
import ReportTimelineChart from "./ReportTimelineChart";

const ReportAssetsContext = createContext("");

const SIGNAL_MEASURE_NOTE =
  "Energy is the player's emotion as they play. A higher line means they are more alert and involved, and a lower line means the moment is flatter. Friction is how hard the moment feels. It rises when play stalls, the same action repeats, or something gets in the way.";

const FRICTION_ONLY_NOTE =
  "Friction is how hard the moment feels. It rises when play stalls, the same action repeats, or something gets in the way.";

function useAssetBaseUrl() {
  return useContext(ReportAssetsContext);
}

function EvidenceClip({ path, label = "Clip" }) {
  const src = resolveMediaUrl(path, useAssetBaseUrl());
  if (!src) return null;
  return (
    <div className="native-clip">
      <span>{label}</span>
      <video controls preload="metadata" src={src} />
    </div>
  );
}

function Section({ title, children, className = "" }) {
  if (!children) return null;
  return (
    <section className={`native-report-section ${className}`.trim()}>
      {title ? <h3 className="native-report-section-title">{title}</h3> : null}
      {children}
    </section>
  );
}

function PriorityBadge({ value }) {
  const label = formatPriority(value);
  if (!label) return null;
  return <span className={`native-priority native-priority--${priorityTone(value)}`}>{label}</span>;
}

function InsightList({ items, titleKey, bodyKey }) {
  const rows = asArray(items);
  if (!rows.length) return null;

  return (
    <ul className="native-insight-list">
      {rows.map((item, index) => {
        const title = typeof item === "string" ? item : asText(item[titleKey]);
        const body = typeof item === "string" ? "" : asText(item[bodyKey]);
        return (
          <li key={`${title}-${index}`} className="native-insight-item">
            <div className="native-insight-head">
              <p className="native-insight-title">{title}</p>
              <PriorityBadge value={item?.priority || item?.severity} />
            </div>
            {body ? <p className="native-insight-body">{body}</p> : null}
            {item?.owner ? <span className="native-chip">{item.owner}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

function suggestionIdeas(text) {
  const raw = asText(text);
  if (!raw) return [];
  const parts = raw
    .split(/\s*(?:\.\s+also\b|,\s*also\s+|;\s+)/i)
    .map((part) => part.trim().replace(/^[,\s]+/, "").replace(/\.$/, ""))
    .filter(Boolean);
  return parts.length ? parts : [raw];
}

function suggestionLabel(category) {
  const value = asText(category).toLowerCase();
  if (value === "ui") return "Interface";
  if (value === "gameplay") return "Gameplay";
  if (value === "other" || !value) return "General";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function SuggestionList({ suggestions, fallback }) {
  const structured = asArray(suggestions).filter((item) => asText(item?.suggestion));
  const rows = structured.length
    ? structured.map((item) => ({
        category: suggestionLabel(item.category),
        ideas: suggestionIdeas(item.suggestion),
      }))
    : asArray(fallback)
        .map((item) => ({ category: "General", ideas: suggestionIdeas(item) }))
        .filter((item) => item.ideas.length);
  if (!rows.length) return null;
  return (
    <div className="native-suggestion-list">
      {rows.map((row, index) => (
        <article key={index} className="native-suggestion-card">
          <span className="native-chip">{row.category}</span>
          {row.ideas.length === 1 ? (
            <p>{row.ideas[0]}</p>
          ) : (
            <ul>
              {row.ideas.map((idea, ideaIndex) => (
                <li key={ideaIndex}>{idea}</li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </div>
  );
}

function ParagraphList({ items }) {
  const rows = asArray(items).map((item) => asText(item)).filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className="native-prose">
      {rows.map((text, index) => (
        <p key={index}>{text}</p>
      ))}
    </div>
  );
}

function FindingCard({ card }) {
  if (!card) return null;
  const evidence = evidenceText(card.evidence);
  const score = scoringLabel(card.scoring);

  return (
    <article className="native-finding-card">
      <div className="native-finding-card-top">
        <PriorityBadge value={card.priority || card.severity} />
        {card.cardType ? <span className="native-chip">{asText(card.cardType)}</span> : null}
        {card.owner ? <span className="native-chip">{asText(card.owner)}</span> : null}
      </div>
      <h4>{asText(card.whatHappened)}</h4>
      {card.whyItMatters ? <p>{asText(card.whyItMatters)}</p> : null}
      <EvidenceClip path={getClipPath(card.evidence)} />
      {evidence && !getClipPath(card.evidence) ? (
        <p className="native-finding-evidence">
          <span>Evidence</span>
          {evidence}
        </p>
      ) : null}
      {card.recommendedAction ? (
        <p className="native-finding-action">
          <span>Action</span>
          {asText(card.recommendedAction)}
        </p>
      ) : null}
      {score ? <p className="native-finding-score">{score}</p> : null}
    </article>
  );
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows?.length) {
    return emptyMessage ? <p className="native-muted">{emptyMessage}</p> : null;
  }
  return (
    <div className="dev-table-wrap native-table-wrap">
      <table className="dev-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || index}>
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render ? column.render(row) : asText(row[column.key]) || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetricStrip({ items }) {
  const visible = items.filter((item) => item.value != null && item.value !== "");
  if (!visible.length) return null;
  return (
    <div className="native-metrics">
      {visible.map((item) => (
        <div key={item.label} className="native-metric">
          <p className="native-metric-label">{item.label}</p>
          <p className="native-metric-value">{metricValue(item.value)}</p>
        </div>
      ))}
    </div>
  );
}

function isDesktopFolderClip(item) {
  const text = `${asText(item?.excerpt)} ${asText(item?.reason)}`.toLowerCase();
  return text.includes("file folder") || text.includes("windows 11");
}

function EvidenceTheme({ theme }) {
  const items = asArray(theme?.items).filter((item) => !isDesktopFolderClip(item));

  return (
    <div className="native-theme">
      <div className="native-theme-heading">
        <span>{asText(theme?.theme)}</span>
        <span>{items.length} clips</span>
      </div>
      {theme?.summary ? <p className="native-theme-summary">{asText(theme.summary)}</p> : null}
      <div className="native-clip-grid">
        {items.map((item, index) => {
          const paths = asArray(item.clipPaths).length ? item.clipPaths : [getClipPath(item)];
          return (
            <article key={`${getClipPath(item)}-${index}`} className="native-clip-card">
              <p className="native-clip-card-title">{asText(item.reason) || asText(item.excerpt)}</p>
              <p className="native-muted">
                {[asText(item.stage), asText(item.reference)].filter(Boolean).join(" · ")}
              </p>
              {item.excerpt && item.reason ? <p>{asText(item.excerpt)}</p> : null}
              {paths.filter(Boolean).map((path) => (
                <EvidenceClip key={asText(path)} path={path} label="Play" />
              ))}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function CohortStrip({ mix }) {
  const kpis = asArray(mix?.kpis);
  if (!kpis.length) return null;
  return (
    <Section title="Cohort metrics">
      <MetricStrip items={kpis.map((item) => ({ label: item.label, value: item.value }))} />
      {asArray(mix?.chips).length ? (
        <div className="native-chip-row">
          {asArray(mix.chips).map((chip) => (
            <span key={asText(chip)} className="native-chip">{asText(chip)}</span>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function SignalNote({ note, timeline }) {
  const text = timelineHasEnergy(timeline) ? asText(note) || SIGNAL_MEASURE_NOTE : FRICTION_ONLY_NOTE;
  return <p className="native-muted">{text}</p>;
}

function ExecSummaryView({ data }) {
  const report = getReportBody(data);
  const brief = getExecutiveBrief(data);

  return (
    <>
      <Section title="Top risks">
        <InsightList items={brief.top3Risks} titleKey="risk" bodyKey="whyItMatters" />
      </Section>
      <Section title="Top opportunities">
        <InsightList items={brief.top3Opportunities} titleKey="opportunity" bodyKey="expectedLift" />
      </Section>
      <div className="native-split">
        <Section title="What changed">
          <ParagraphList items={brief.whatChangedSinceLastRun} />
        </Section>
        <Section title="Next sprint">
          <ParagraphList items={brief.nextSprintActions} />
        </Section>
      </div>
      {!brief.top3Risks?.length && report.executiveSummary ? (
        <Section title="Executive summary">
          <ParagraphList items={report.executiveSummary} />
        </Section>
      ) : null}
    </>
  );
}

function GameReportView({ data }) {
  const report = getReportBody(data);
  const meta = getMeta(data);
  const brief = getExecutiveBrief(data);

  return (
    <>
      <header className="native-report-hero">
        <h3>{asText(report.reportTitle) || "Playtest overview"}</h3>
        <MetricStrip
          items={[
            { label: "Quality gate", value: data?.qualityGate?.score != null ? `${data.qualityGate.score}/${data.qualityGate.maxScore || 9}` : null },
            { label: "Feedback", value: meta.feedbackRows || data?.playerFeedback?.rows },
            { label: "Bugs", value: meta.feedbackBugReports || data?.playerFeedback?.bugReportsCount },
          ]}
        />
      </header>
      <Section title="Executive summary">
        <ParagraphList items={report.executiveSummary || brief.top3Risks} />
      </Section>
      <CohortStrip mix={meta.cohortMix} />
      {asArray(data?.timeline).length > 0 ? (
        <Section title="Session momentum">
          <SignalNote note={data?.signalMeasureNote || meta.signalMeasureNote} timeline={data.timeline} />
          <ReportTimelineChart timeline={data.timeline} emphasizeShape />
        </Section>
      ) : null}
      <Section title="Key moments">
        <SimpleTable
          columns={[
            { key: "stage", label: "Stage" },
            { key: "label", label: "Moment" },
            { key: "explanation", label: "What happened" },
            {
              key: "importance",
              label: "Priority",
              render: (row) => <PriorityBadge value={row.importance || row.priority} />,
            },
          ]}
          rows={asArray(report.timelineEvents)}
        />
      </Section>
      <Section title="Evidence-first findings">
        <div className="native-finding-grid">
          {asArray(report.findingCards).map((card, index) => (
            <FindingCard key={index} card={card} />
          ))}
        </div>
      </Section>
      <div className="native-split">
        <Section title="Portfolio risks">
          <InsightList items={report.portfolioRisks} titleKey="risk" bodyKey="whyItMatters" />
        </Section>
        <Section title="Portfolio opportunities">
          <InsightList items={report.portfolioOpportunities} titleKey="opportunity" bodyKey="expectedLift" />
        </Section>
      </div>
      <Section title="Bugs detected">
        <SimpleTable
          columns={[
            { key: "title", label: "Bug" },
            {
              key: "severity",
              label: "Severity",
              render: (row) => <PriorityBadge value={row.severity} />,
            },
            { key: "description", label: "Description" },
            { key: "playerEvidence", label: "Evidence" },
          ]}
          rows={asArray(report.bugsDetected)}
          emptyMessage="No bugs were detected."
        />
      </Section>
      <Section title="Player suggestions">
        <SuggestionList
          suggestions={data?.playerFeedback?.suggestions}
          fallback={report.playerSuggestionContext || data?.playerFeedback?.comments}
        />
      </Section>
      <Section title="Decision backlog">
        <SimpleTable
          columns={[
            {
              key: "priority",
              label: "Priority",
              render: (row) => <PriorityBadge value={row.priority} />,
            },
            { key: "hypothesis", label: "Hypothesis" },
            { key: "experiment", label: "Experiment" },
            { key: "successMetric", label: "Success metric" },
          ]}
          rows={asArray(report.decisionBacklog)}
        />
      </Section>
      {report.methodologyScope ? (
        <Section title="Methodology and scope">
          <p className="native-muted">
            Window {asText(report.methodologyScope.windowSeconds) || "5"}s · Sources{" "}
            {asArray(report.methodologyScope.dataSourcesUsed).map((source) => asText(source)).filter(Boolean).join(", ") || "—"}
          </p>
          {report.methodologyScope.outOfScope ? <p className="native-muted">{report.methodologyScope.outOfScope}</p> : null}
        </Section>
      ) : null}
    </>
  );
}

function BehavioralView({ data }) {
  const report = getReportBody(data);
  const meta = getMeta(data);

  return (
    <>
      <header className="native-report-hero">
        <h3>{asText(report.reportTitle) || "Behavioral analysis"}</h3>
        <MetricStrip
          items={[
            { label: "Quality gate", value: data?.qualityGate?.score != null ? `${data.qualityGate.score}/${data.qualityGate.maxScore || 10}` : null },
          ]}
        />
      </header>
      <Section title="Executive summary">
        <ParagraphList items={report.executiveSummary} />
      </Section>
      <Section title="Behavioral profile">
        <ParagraphList items={report.behaviorProfileOverview} />
      </Section>
      <CohortStrip mix={meta.cohortMix || data?.gameMeta?.cohortMix} />
      {asArray(data?.timelineSummary || data?.timeline).length > 0 ? (
        <Section title="Behavioral timeline">
          <SignalNote
            note={data?.signalMeasureNote || meta.signalMeasureNote}
            timeline={data.timelineSummary || data.timeline}
          />
          <ReportTimelineChart timeline={data.timelineSummary || data.timeline} emphasizeShape />
        </Section>
      ) : null}
      <Section title="Key moments">
        <SimpleTable
          columns={[
            { key: "stage", label: "Stage" },
            { key: "label", label: "Moment" },
            { key: "explanation", label: "What happened" },
          ]}
          rows={asArray(report.timelineEvents)}
        />
      </Section>
      <Section title="Segment comparisons">
        <SimpleTable
          columns={[
            { key: "segment", label: "Segment" },
            { key: "difference", label: "Difference" },
            { key: "implication", label: "Implication" },
          ]}
          rows={asArray(report.segmentComparisons)}
        />
      </Section>
      <Section title="Evidence-first findings">
        <div className="native-finding-grid">
          {asArray(report.findingCards).map((card, index) => (
            <FindingCard key={index} card={card} />
          ))}
        </div>
      </Section>
      <div className="native-split">
        <Section title="Priority risks">
          <InsightList items={report.priorityRisks} titleKey="risk" bodyKey="whyItMatters" />
        </Section>
        <Section title="Opportunities">
          <InsightList items={report.opportunities} titleKey="opportunity" bodyKey="expectedLift" />
        </Section>
      </div>
      <Section title="Decision backlog">
        <SimpleTable
          columns={[
            {
              key: "priority",
              label: "Priority",
              render: (row) => <PriorityBadge value={row.priority} />,
            },
            { key: "hypothesis", label: "Hypothesis" },
            { key: "experiment", label: "Experiment" },
            { key: "successMetric", label: "Success metric" },
          ]}
          rows={asArray(report.decisionBacklog)}
        />
      </Section>
      <Section title="Confidence and method">
        {report.methodologyNote ? <p className="native-muted">{asText(report.methodologyNote)}</p> : null}
        <ParagraphList items={report.confidenceNotes} />
      </Section>
      {report.methodologyScope ? (
        <Section title="Methodology and scope">
          <p className="native-muted">
            Window {asText(report.methodologyScope.windowSeconds) || "5"}s · Sources{" "}
            {asArray(report.methodologyScope.dataSourcesUsed).map((source) => asText(source)).filter(Boolean).join(", ") || "—"}
          </p>
          {report.methodologyScope.outOfScope ? <p className="native-muted">{report.methodologyScope.outOfScope}</p> : null}
        </Section>
      ) : null}
    </>
  );
}

function WorkbookView({ data }) {
  const workbook = getWorkbook(data);
  const themes = asArray(workbook.evidenceThemes);

  return (
    <>
      <header className="native-report-hero">
        <h3>{asText(workbook.workbookTitle) || "Evidence workbook"}</h3>
      </header>
      <Section title="Coverage">
        <ParagraphList items={workbook.coverageSummary} />
      </Section>
      <Section title="Segment observations">
        <SimpleTable
          columns={[
            { key: "segment", label: "Segment" },
            { key: "summary", label: "Summary" },
            {
              key: "confidence",
              label: "Confidence",
              render: (row) => <span className="native-chip">{asText(row.confidence)}</span>,
            },
          ]}
          rows={asArray(workbook.segmentObservations)}
        />
      </Section>
      <Section title="Evidence themes">
        {themes.map((theme, index) => (
          <EvidenceTheme key={theme.theme || index} theme={theme} />
        ))}
      </Section>
    </>
  );
}

function GenreBenchmarkView({ data }) {
  const readiness = data?.launchReadiness || {};

  return (
    <>
      <header className="native-report-hero">
        <h3>Genre benchmark</h3>
        <MetricStrip
          items={[
            { label: "Launch readiness", value: readiness.score != null ? `${readiness.score}/100` : null },
            { label: "Readiness", value: readiness.label },
          ]}
        />
        {data?.executiveSignal ? <p className="native-muted">{asText(data.executiveSignal)}</p> : null}
        {data?.sessionCritic?.launchMeaning ? <p>{asText(data.sessionCritic.launchMeaning)}</p> : null}
      </header>
      <CohortStrip mix={data?.cohortMix} />
      <Section title="Opportunity gap analysis">
        {data?.gapDescription ? <p className="native-muted">{asText(data.gapDescription)}</p> : null}
        <SimpleTable
          columns={[
            { key: "label", label: "Dimension" },
            { key: "score", label: "Current" },
            { key: "targetZone", label: "Strong-session bar" },
            { key: "gap", label: "Gap" },
            { key: "meaning", label: "Meaning" },
          ]}
          rows={asArray(data?.dimensions)}
        />
      </Section>
      <Section title="Session momentum">
        <SignalNote note={data?.signalMeasureNote} timeline={data.timeline || data.timelineSummary} />
        {asArray(data?.timeline || data?.timelineSummary).length > 0 ? (
          <ReportTimelineChart timeline={data.timeline || data.timelineSummary} emphasizeShape />
        ) : null}
        <SimpleTable
          columns={[
            { key: "moment", label: "Moment" },
            { key: "currentSignal", label: "Current signal" },
            { key: "benchmarkPattern", label: "Benchmark pattern" },
            { key: "interpretation", label: "Interpretation" },
          ]}
          rows={asArray(data?.sessionMomentum)}
        />
      </Section>
      <Section title="Evidence to design hypothesis">
        <SimpleTable
          columns={[
            { key: "observedSignal", label: "Observed signal" },
            { key: "playerMeaning", label: "Player meaning" },
            { key: "benchmarkGap", label: "Benchmark gap" },
            { key: "designHypothesis", label: "Design hypothesis" },
            {
              key: "priority",
              label: "Priority",
              render: (row) => <PriorityBadge value={row.priority} />,
            },
            {
              key: "clipPath",
              label: "Clip",
              render: (row) => {
                const paths = asArray(row.clipPaths).length ? row.clipPaths : [row.clipPath];
                return (
                  <div className="native-clip-stack">
                    {paths.filter(Boolean).map((path) => (
                      <EvidenceClip key={asText(path)} path={path} label="Clip" />
                    ))}
                  </div>
                );
              },
            },
          ]}
          rows={asArray(data?.evidenceToDesignHypotheses)}
        />
      </Section>
      <Section title="Playable evidence clips">
        <div className="native-clip-grid">
          {asArray(data?.evidenceClips).map((clip, index) => (
            <div key={`${getClipPath(clip)}-${index}`} className="native-clip-card">
              <p>{asText(clip.title || clip.behavior)}</p>
              <EvidenceClip path={getClipPath(clip)} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="Prioritized fix roadmap">
        <SimpleTable
          columns={[
            { key: "order", label: "Order" },
            { key: "title", label: "Fix" },
            { key: "description", label: "Experiment" },
            {
              key: "priority",
              label: "Priority",
              render: (row) => <PriorityBadge value={row.priority} />,
            },
            { key: "status", label: "Status" },
          ]}
          rows={asArray(data?.prioritizedFixRoadmap)}
        />
      </Section>
      <Section title="Retest plan">
        <SimpleTable
          columns={[
            { key: "metric", label: "Metric" },
            { key: "target", label: "Target" },
          ]}
          rows={asArray(data?.retestPlan)}
        />
      </Section>
      <Section title="Methodology and confidence">
        <SimpleTable
          columns={[
            { key: "area", label: "Area" },
            { key: "detail", label: "Detail" },
          ]}
          rows={asArray(data?.methodology)}
        />
      </Section>
    </>
  );
}

function EvidenceAppendixView({ data }) {
  return (
    <>
      <header className="native-report-hero">
        <h3>{asText(data?.gameName) ? `${asText(data.gameName)} evidence appendix` : "Evidence appendix"}</h3>
        {data?.cohortLabel ? <p className="native-muted">{asText(data.cohortLabel)}</p> : null}
      </header>
      <Section title="Stage breakdown">
        <SimpleTable
          columns={[
            { key: "stage", label: "Stage" },
            { key: "dominantBehavior", label: "Dominant behavior" },
            { key: "riskRatio", label: "Risk ratio" },
            { key: "example", label: "Example" },
          ]}
          rows={asArray(data?.sectionBreakdown)}
        />
      </Section>
      <Section title="Friction hotspots">
        <SimpleTable
          columns={[
            { key: "behavior", label: "Behavior" },
            { key: "count", label: "Count" },
            { key: "sampleRange", label: "Sample range" },
            { key: "sampleEvidence", label: "Evidence" },
          ]}
          rows={asArray(data?.frictionHotspots)}
        />
      </Section>
      <Section title="Player behavior breakdown">
        <SimpleTable
          columns={[
            { key: "dominantBehavior", label: "Dominant behavior" },
            { key: "riskWindowRatio", label: "Risk ratio" },
            { key: "example", label: "Example" },
          ]}
          rows={asArray(data?.playerBehaviorBreakdown)}
        />
      </Section>
      <Section title="Playable evidence clips">
        <div className="native-clip-grid">
          {asArray(data?.evidenceClips).map((clip, index) => {
            const paths = asArray(clip.clipPaths).length
              ? clip.clipPaths
              : [clip.videoPath || clip.clipPath];
            return (
              <div key={`${getClipPath(clip)}-${index}`} className="native-clip-card">
                <p>{asText(clip.behavior || clip.title)}</p>
                {clip.startSecond != null && clip.endSecond != null ? (
                  <p className="native-muted">
                    {asText(clip.startSecond)}s to {asText(clip.endSecond)}s
                  </p>
                ) : null}
                {paths.filter(Boolean).map((path) => (
                  <EvidenceClip key={asText(path)} path={path} label="Clip" />
                ))}
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}

function NativeReportView({ data, viewKind, assetBaseUrl = "" }) {
  const content = useMemo(() => {
    if (!data) return null;
    if (viewKind === "exec_summary") return <ExecSummaryView data={data} />;
    if (viewKind === "behavioral_analysis") return <BehavioralView data={data} />;
    if (viewKind === "evidence_workbook") return <WorkbookView data={data} />;
    if (viewKind === "genre_benchmark") return <GenreBenchmarkView data={data} />;
    if (viewKind === "evidence_appendix") return <EvidenceAppendixView data={data} />;
    return <GameReportView data={data} />;
  }, [data, viewKind]);

  return (
    <ReportAssetsContext.Provider value={assetBaseUrl || ""}>
      <div className="native-report">{content}</div>
    </ReportAssetsContext.Provider>
  );
}

export default NativeReportView;
